import { useEffect, useState } from "react";

/**
 * GeneratorDemo — auto-playing, looping CSS/JS demo of the Atomic Edu
 * generator. Built using the site's existing visual language
 * (Montserrat, navy / slate / mint / light-gray) so it reads as the
 * actual tool, not a stylized mock.
 *
 * Timeline:
 *   0.0–2.0s   Phase 1 · Empty form fades in
 *   2.0–5.0s   Phase 2 · Scan — cursor selects Skill Focus / Grade / Subject
 *   5.0–7.0s   Phase 3 · Process — type the context prompt char-by-char
 *   7.0–8.0s   Phase 4 · Execute — cursor clicks the button, button loads
 *   8.0–13.0s  Phase 5 · Output card fades in
 *   13.0–16.0s Phase 6 · Hold, then reset and loop
 */
const CYCLE_MS = 16000;
const TYPE_TEXT = "Students struggle to explain their reasoning out loud.";

// 12 cursor positions in *percent* of the frame so it scales cleanly.
// Each entry: { t (ms from cycle start), x %, y % }.
const CURSOR_PATH = [
  { t: 0, x: 50, y: 92 },
  { t: 1900, x: 12, y: 24 }, // -> Skill Focus
  { t: 3000, x: 38, y: 24 }, // -> Grade
  { t: 4100, x: 65, y: 24 }, // -> Subject
  { t: 5200, x: 18, y: 52 }, // -> Prompt field
  { t: 7100, x: 50, y: 78 }, // -> Generate button
  { t: 8100, x: 50, y: 78 }, // (hold)
];

function useCursorPosition() {
  const [pos, setPos] = useState(CURSOR_PATH[0]);

  useEffect(() => {
    let raf;
    const start = performance.now();

    const tick = () => {
      const elapsed = (performance.now() - start) % CYCLE_MS;
      // Find the current segment of the path
      let current = CURSOR_PATH[0];
      for (let i = 0; i < CURSOR_PATH.length; i += 1) {
        if (CURSOR_PATH[i].t <= elapsed) {
          current = CURSOR_PATH[i];
        } else {
          break;
        }
      }
      setPos(current);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return pos;
}

function usePhase() {
  const [phase, setPhase] = useState(1);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = () => {
      const t = (performance.now() - start) % CYCLE_MS;
      let next = 1;
      if (t >= 13000) next = 6;
      else if (t >= 8000) next = 5;
      else if (t >= 7000) next = 4;
      else if (t >= 5000) next = 3;
      else if (t >= 2000) next = 2;
      setPhase(next);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  return phase;
}

function useTypedText(activePhase) {
  const [text, setText] = useState("");
  useEffect(() => {
    if (activePhase < 3) {
      setText("");
      return undefined;
    }
    if (activePhase >= 4) {
      setText(TYPE_TEXT);
      return undefined;
    }
    // Active typing during phase 3 — type the chars across ~1.8s
    const start = performance.now();
    const duration = 1800;
    let raf;
    const tick = () => {
      const t = performance.now() - start;
      const frac = Math.min(1, t / duration);
      const n = Math.floor(frac * TYPE_TEXT.length);
      setText(TYPE_TEXT.slice(0, n));
      if (frac < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [activePhase]);
  return text;
}

// ---------- Sub-components ---------------------------------------------

const DemoDropdown = ({ label, value, active, selected }) => (
  <div
    className={`flex-1 min-w-0 rounded-md border bg-white px-3 py-2 transition-all duration-300 ${
      active && !selected
        ? "border-[#96ded1] ring-2 ring-[#96ded1]/30 shadow-sm"
        : selected
        ? "border-[#96ded1]/50"
        : "border-[#4a6984]/15"
    }`}
  >
    <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#4a6984]/60">
      {label}
    </p>
    <p
      className={`mt-1 text-xs font-semibold truncate transition-colors duration-300 ${
        value ? "text-[#1c2d3d]" : "text-[#4a6984]/40"
      }`}
    >
      {value || "Select"}
    </p>
  </div>
);

const OutputCard = ({ visible }) => (
  <div
    data-testid="demo-output-card"
    className={`absolute inset-x-4 bottom-4 ae-card border-l-[3px] border-l-[#96ded1] p-4 shadow-xl transition-all duration-700 ${
      visible
        ? "opacity-100 translate-y-0"
        : "opacity-0 translate-y-3 pointer-events-none"
    }`}
    style={{ transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)" }}
  >
    <div className="flex items-center justify-between gap-2">
      <span className="text-[9px] font-bold uppercase tracking-[0.25em] text-[#4a6984]/70">
        Lesson Output
      </span>
      <span className="ae-pill text-[9px]">Grade 6 · Science</span>
    </div>
    <h3 className="mt-2 font-extrabold text-[15px] text-[#1c2d3d] tracking-tight">
      The Reasoning Room
    </h3>
    <p className="mt-1 text-[11px] text-[#4a6984]/80 font-medium leading-snug">
      Students defend their thinking — out loud, under pressure.
    </p>
    <div className="mt-2.5 flex flex-wrap gap-1.5">
      {["Critical Thinking", "Collaboration", "Self-Regulation"].map((s) => (
        <span key={s} className="ae-pill text-[9px]">
          {s}
        </span>
      ))}
    </div>
    <div className="mt-3 border-t border-[#4a6984]/10 pt-2.5">
      <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#4a6984]/70">
        Career Connection
      </p>
      <p className="mt-1 text-[11px] text-[#1c2d3d] font-medium leading-snug">
        This is what a scientist does when presenting findings to a review
        panel. Your students are practising that today.
      </p>
    </div>
  </div>
);

// ---------- Main component ---------------------------------------------

export const GeneratorDemo = () => {
  const phase = usePhase();
  const cursor = useCursorPosition();
  const typed = useTypedText(phase);

  const skillFocusValue = phase >= 2 && cursor.x >= 12 ? "Critical Thinking" : "";
  // Dropdowns lock in once the cursor has visited them. Index 0=skill,1=grade,2=subject.
  const skillFocusSelected = phase >= 2 && cursor.t >= 1900;
  const gradeSelected = phase >= 2 && cursor.t >= 3000;
  const subjectSelected = phase >= 2 && cursor.t >= 4100;

  const skill = skillFocusSelected ? "Critical Thinking" : "";
  const grade = gradeSelected ? "Grade 6" : "";
  const subject = subjectSelected ? "Science" : "";

  const promptActive = phase === 3;
  const buttonActive = phase === 4;
  const buttonLoading = phase === 4;
  const outputVisible = phase >= 5;

  return (
    <section
      data-testid="generator-demo-section"
      className="relative z-10 pt-10 pb-16 md:pt-14 md:pb-20"
    >
      <div className="max-w-[820px] mx-auto px-6">
        {/* Browser-style mockup frame */}
        <div
          data-testid="generator-demo-frame"
          className="relative mx-auto rounded-xl shadow-2xl shadow-[#1c2d3d]/15 bg-white overflow-hidden border border-[#4a6984]/10"
          style={{ aspectRatio: "16 / 11", maxWidth: "720px" }}
        >
          {/* Browser chrome */}
          <div className="flex items-center gap-1.5 px-3 py-2 bg-[#f4f5f7] border-b border-[#4a6984]/10">
            <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]/70" />
            <div className="flex-1 mx-3">
              <div className="h-4 rounded bg-white/80 border border-[#4a6984]/10 flex items-center justify-center">
                <span className="text-[9px] font-semibold text-[#4a6984]/70 tracking-wide">
                  atomicedu.ca
                </span>
              </div>
            </div>
          </div>

          {/* Form body — its visibility is gated by phase >= 5 to clear */}
          <div
            className={`absolute inset-x-0 top-9 bottom-0 px-5 py-4 transition-opacity duration-500 ${
              outputVisible ? "opacity-30" : "opacity-100"
            }`}
          >
            {/* Three dropdowns row */}
            <div className="flex items-stretch gap-2">
              <DemoDropdown
                label="Skill Focus"
                value={skill}
                active={phase === 2 && cursor.t < 3000}
                selected={skillFocusSelected}
              />
              <DemoDropdown
                label="Grade"
                value={grade}
                active={phase === 2 && cursor.t >= 3000 && cursor.t < 4100}
                selected={gradeSelected}
              />
              <DemoDropdown
                label="Subject"
                value={subject}
                active={phase === 2 && cursor.t >= 4100 && cursor.t < 5200}
                selected={subjectSelected}
              />
            </div>

            {/* Prompt textarea */}
            <div
              className={`mt-4 rounded-md border bg-white px-3 py-2 transition-all duration-300 ${
                promptActive
                  ? "border-[#96ded1] ring-2 ring-[#96ded1]/30 shadow-sm"
                  : typed
                  ? "border-[#96ded1]/50"
                  : "border-[#4a6984]/15"
              }`}
            >
              <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#4a6984]/60">
                Describe your class or context (optional)
              </p>
              <p
                className="mt-1.5 text-xs font-medium text-[#1c2d3d] leading-snug min-h-[1.5em]"
                data-testid="demo-prompt-text"
              >
                {typed}
                {promptActive && (
                  <span className="inline-block w-[1.5px] h-[0.9em] bg-[#1c2d3d] align-middle ml-0.5 animate-pulse" />
                )}
              </p>
            </div>

            {/* Generate button */}
            <div className="mt-4 flex justify-center">
              <div
                className={`relative inline-flex items-center justify-center gap-2 h-10 px-6 rounded-lg text-xs font-bold transition-all duration-300 ${
                  buttonActive
                    ? "bg-[#1c2d3d] text-white scale-[1.02]"
                    : "bg-[#96ded1] text-[#1c2d3d]"
                }`}
              >
                {buttonLoading ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />
                    <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse [animation-delay:0.15s]" />
                    <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse [animation-delay:0.3s]" />
                  </span>
                ) : (
                  <>
                    Generate activity
                    <span className="text-[10px]">→</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Output card */}
          <OutputCard visible={outputVisible} />

          {/* Cursor */}
          <div
            data-testid="demo-cursor"
            aria-hidden="true"
            className="absolute pointer-events-none z-20"
            style={{
              left: `${cursor.x}%`,
              top: `${cursor.y}%`,
              transform: "translate(-30%, -10%)",
              transition: "left 900ms cubic-bezier(0.45, 0, 0.2, 1), top 900ms cubic-bezier(0.45, 0, 0.2, 1)",
            }}
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 2L3 17L7.5 13.2L10 19L13 17.7L10.5 12L17 11.5L3 2Z"
                fill="#1c2d3d"
                stroke="white"
                strokeWidth="1.4"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {/* Illustrative disclaimer — animation, not the live tool */}
        <p
          data-testid="generator-demo-disclaimer"
          className="mt-3 text-center text-[11px] text-[#758d9f]"
        >
          Illustration of the generator interface.
        </p>

        {/* Caption */}
        <p
          data-testid="generator-demo-caption"
          className="mt-6 text-center font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]/70"
        >
          A classroom-ready activity — in under 60 seconds.
        </p>
      </div>
    </section>
  );
};

export default GeneratorDemo;
